/**
 * The three "there is no CRM data" sentinels the analyze step puts inside
 * `<crm_context>`, mirrored here for the rubric that has to recognise them.
 *
 * The producer is `apps/workflows-api/src/workers/analyze/crm-context.ts`,
 * which exports the same three values. A package cannot import from an app, so
 * nothing in the type system links producer and consumer: renaming
 * `CRM_UNAVAILABLE` there, or adding a fourth flavour, leaves TypeScript clean
 * and `isCrmContextMissing` working, while the STEP 1 rubric in
 * analysis-guidelines.ts silently stops matching the content it is shown. The
 * model then falls through to STEP 2 and grades `crm_completeness` data
 * hygiene on a call with no CRM behind it — precisely the regression these
 * sentinels replaced the bare 'N/A' to end.
 *
 * `test/crm-sentinel-drift.test.ts` is that missing link: it reads the
 * producer's source and fails if these values or their count drift. Do not
 * inline the strings back into the rubric text — the test can only pin what it
 * can find in one place.
 */

/** The org has no CRM integration — data hygiene has nothing to measure against. */
export const CRM_NOT_CONNECTED = 'CRM_NOT_CONNECTED';

/** CRM is configured, but this record is linked to no entity — a new customer. */
export const CRM_NO_LINKED_ENTITY = 'CRM_NO_LINKED_ENTITY';

/** Lookup failed (missing record, provider error) — we know nothing either way. */
export const CRM_CONTEXT_UNAVAILABLE = 'CRM_UNAVAILABLE';

/** All three, for drift checks. */
export const CRM_CONTEXT_SENTINELS = [
  CRM_NOT_CONNECTED,
  CRM_NO_LINKED_ENTITY,
  CRM_CONTEXT_UNAVAILABLE,
] as const;
